import { Check, Link2, RefreshCw, RotateCcw, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  useReplacementCandidates,
  useReplacementLiveState,
  useSetReplacedContract,
  useUnverifyReplacement,
  useVerifyReplacement,
  type ReplacementCandidate,
  type ReplacementRow,
} from "../api";
import { fmtCur2, fmtDate } from "../../renewals/format";

/** Settle one replacement: what it superseded, and what it netted.
 *
 *  This is the thing that retires a row from R-08's backlog — permanently and
 *  in every future month, because the record is durable and not period-scoped.
 *  So it asks for more than a click:
 *
 *  - **the link has to exist in Cinch.** We never hold a local one; the mart,
 *    the KPI tiles, renewals and commissions all read it from Cinch, so a link
 *    only we knew about would be invisible to every one of them. When it's
 *    missing, the picker suggests candidates and the write goes to Cinch.
 *  - **the net can be overridden, with a reason.** That's what makes the
 *    reseller instruments verifiable at all — no formula fits a PO whose
 *    account is the reseller.
 *  - **"replaces nothing" is a real answer**, needing a note. Some contracts are
 *    typed as replacements and aren't. */
export function ReplacementVerifyPanel({
  period,
  row,
  canAct,
}: {
  period: string;
  row: ReplacementRow;
  canAct: boolean;
}) {
  const verify = useVerifyReplacement(period);
  const unverify = useUnverifyReplacement(period);
  const setLink = useSetReplacedContract(period);
  const [checkLive, setCheckLive] = useState(false);
  const live = useReplacementLiveState(period, row.contract_id, checkLive);

  const [overriding, setOverriding] = useState(false);
  const [netText, setNetText] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  const linked = !!row.replaced_contract_id || !!live.data?.replaced_contract_id;
  const v = row.verification;

  function submit(status: "verified" | "no_replacement") {
    const override = overriding && netText.trim() !== "";
    verify.mutate(
      {
        contractId: row.contract_id,
        status,
        net_change_mrr: override ? Number(netText) : null,
        override_reason: override ? reason : null,
        notes: notes || null,
      },
      {
        onSuccess: () =>
          toast.success(
            status === "verified"
              ? "Replacement verified — off the backlog"
              : "Recorded as replacing nothing",
          ),
        onError: (e) =>
          toast.error("Couldn't verify", { description: (e as Error).message }),
      },
    );
  }

  // ── already settled ──
  if (v && !v.stale) {
    return (
      <div className="space-y-2 rounded-md border border-success/40 bg-success-light/30 px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-success">
            <Check className="h-4 w-4" />
            {v.status === "verified" ? "Verified" : "Replaces nothing"}
          </span>
          {canAct && (
            <Button
              variant="ghost"
              size="sm"
              disabled={unverify.isPending}
              onClick={() =>
                unverify.mutate(row.contract_id, {
                  onSuccess: () => toast.success("Reopened"),
                })
              }
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reopen
            </Button>
          )}
        </div>
        <dl className="space-y-1 text-xs text-muted-foreground">
          {v.status === "verified" && (
            <div className="flex justify-between gap-2">
              <dt>Net change</dt>
              <dd className="font-semibold tabular-nums text-foreground">
                {fmtCur2(v.net_change_mrr)}/mo
                {v.net_override && (
                  <span className="ml-1 text-warning">(override)</span>
                )}
              </dd>
            </div>
          )}
          {v.override_reason && <p>Override: {v.override_reason}</p>}
          {v.notes && <p>{v.notes}</p>}
          <p>
            Settled by {v.verified_by_name ?? "—"}
            {v.verified_at && ` on ${new Date(v.verified_at).toLocaleDateString()}`}
            {v.verified_in_period && ` · ${v.verified_in_period} close`}
          </p>
        </dl>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-md border px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Verify
        </span>
        {v?.stale && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-warning">
            <TriangleAlert className="h-3.5 w-3.5" /> Reopened — the numbers changed
          </span>
        )}
      </div>

      {v?.stale && (
        <p className="rounded-md border border-warning/40 bg-warning-light/40 px-3 py-2 text-xs text-warning">
          Previously settled at {fmtCur2(v.net_change_mrr)}/mo by{" "}
          {v.verified_by_name ?? "—"}. The contract has changed since, so it needs
          settling again.
        </p>
      )}

      {!linked ? (
        <MissingLink
          period={period}
          row={row}
          canAct={canAct}
          onSetLink={(candidateId) =>
            setLink.mutate(
              { contractId: row.contract_id, replacedContractId: candidateId },
              {
                onSuccess: () => {
                  toast.success("Link set in Cinch");
                  setCheckLive(true);
                },
                onError: (e) =>
                  toast.error("Couldn't set the link in Cinch", {
                    description: (e as Error).message,
                  }),
              },
            )
          }
          settingLink={setLink.isPending}
          live={live.data ?? null}
          liveLoading={live.isFetching}
          onCheckLive={() => setCheckLive(true)}
        />
      ) : (
        <p className="text-xs text-muted-foreground">
          Supersedes{" "}
          <b className="text-foreground">
            {row.replaced_company_name ??
              live.data?.replaced_company_name ??
              "a linked contract"}
          </b>{" "}
          — {fmtCur2(row.replaced_stack_mrr)}/mo. Computed net{" "}
          <b className="text-foreground">{fmtCur2(row.net_change_mrr)}/mo</b>.
        </p>
      )}

      {/* Override — the escape hatch that makes reseller instruments settleable */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={overriding}
            disabled={!canAct}
            onChange={(e) => setOverriding(e.target.checked)}
          />
          Override the computed net
          {!row.valued && (
            <span className="text-warning">
              — needed here, no baseline is computable
            </span>
          )}
        </label>
        {overriding && (
          <div className="space-y-2">
            <Input
              type="number"
              step="0.01"
              placeholder="Net change MRR, e.g. 1430.00"
              value={netText}
              disabled={!canAct}
              onChange={(e) => setNetText(e.target.value)}
            />
            <Input
              placeholder="Why the computed figure is wrong (required)"
              value={reason}
              disabled={!canAct}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        )}
      </div>

      <Textarea
        rows={2}
        placeholder="Notes (required to record it as replacing nothing)"
        value={notes}
        disabled={!canAct}
        onChange={(e) => setNotes(e.target.value)}
        aria-label="Verification notes"
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={() => submit("verified")}
          disabled={!canAct || !linked || verify.isPending}
          title={
            linked
              ? undefined
              : "Nothing is linked in Cinch yet — set the link first, or record it as replacing nothing"
          }
        >
          <Check className="mr-1 h-3.5 w-3.5" />
          {verify.isPending ? "Saving…" : "Verify"}
        </Button>
        <Button
          variant="outline"
          onClick={() => submit("no_replacement")}
          disabled={!canAct || verify.isPending}
        >
          Replaces nothing
        </Button>
        {!canAct && (
          <span className="text-xs text-muted-foreground">
            Only a RevOps or Finance owner can settle this.
          </span>
        )}
      </div>
    </div>
  );
}

/** The 8-of-42 case: nothing linked, so nothing can be verified until it is.
 *  Suggests candidates, and offers the Cinch write to whoever holds billing
 *  access. */
function MissingLink({
  period,
  row,
  canAct,
  onSetLink,
  settingLink,
  live,
  liveLoading,
  onCheckLive,
}: {
  period: string;
  row: ReplacementRow;
  canAct: boolean;
  onSetLink: (candidateId: string) => void;
  settingLink: boolean;
  live: { available: boolean; drift: string[]; error: string | null } | null;
  liveLoading: boolean;
  onCheckLive: () => void;
}) {
  const q = useReplacementCandidates(period, row.contract_id);
  const [picked, setPicked] = useState<string | null>(null);

  return (
    <div className="space-y-2 rounded-md border border-warning/40 bg-warning-light/30 px-3 py-2">
      <p className="text-xs text-warning">
        <b>Nothing linked in Cinch.</b> This can't be verified until the
        superseded contract is recorded there — the mart, the KPI tiles and
        commissions all read the link from Cinch, so it has to be set at source.
      </p>

      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onCheckLive} disabled={liveLoading}>
          <RefreshCw className={cn("mr-1 h-3.5 w-3.5", liveLoading && "animate-spin")} />
          Check Cinch now
        </Button>
        <span className="text-[11px] text-muted-foreground">
          Snowflake is up to a day behind — use this if you just set it.
        </span>
      </div>
      {live && !live.available && live.error && (
        <p className="text-[11px] text-muted-foreground">{live.error}</p>
      )}

      {q.isLoading && <div className="h-16 animate-pulse rounded bg-muted/40" />}
      {q.data && q.data.candidates.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No compatible contract on this account
          {q.data.allowed_types.length > 0 && (
            <> (a {q.data.contract_type_name} can only replace{" "}
            {q.data.allowed_types.join(" or ")})</>
          )}
          . If it really supersedes nothing, record that below.
        </p>
      )}
      {q.data && q.data.candidates.length > 0 && (
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Likely superseded
          </p>
          <ul className="space-y-1">
            {q.data.candidates.slice(0, 5).map((c) => (
              <CandidateRow
                key={c.contract_id}
                c={c}
                selected={picked === c.contract_id}
                onSelect={() => setPicked(c.contract_id)}
              />
            ))}
          </ul>
          <Button
            size="sm"
            className="mt-1"
            disabled={!picked || !canAct || settingLink}
            onClick={() => picked && onSetLink(picked)}
            title="Writes the link to Cinch — needs billing access (superuser)"
          >
            <Link2 className="mr-1 h-3.5 w-3.5" />
            {settingLink ? "Setting in Cinch…" : "Set link in Cinch"}
          </Button>
        </div>
      )}
    </div>
  );
}

function CandidateRow({
  c,
  selected,
  onSelect,
}: {
  c: ReplacementCandidate;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded border px-2 py-1.5 text-left text-xs transition-colors",
          selected ? "border-brand bg-brand/5" : "border-border hover:bg-muted",
        )}
      >
        <span className="min-w-0">
          <span className="block truncate font-medium">{c.company_name ?? "—"}</span>
          <span className="block truncate text-[11px] text-muted-foreground">
            {c.contract_type_name} · {fmtCur2(c.mrr_total)}/mo ·{" "}
            {c.days_before_handoff === 1
              ? "ended the day before this one started"
              : c.was_open_at_handoff
                ? "still running at the handoff"
                : c.billing_effective_through
                  ? `ended ${fmtDate(c.billing_effective_through)}`
                  : "no dates"}
          </span>
        </span>
        <span
          className={cn(
            "flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium",
            c.confidence === "high"
              ? "bg-success-light text-success"
              : c.confidence === "medium"
                ? "bg-warning-light text-warning"
                : "bg-muted text-muted-foreground",
          )}
        >
          {c.confidence}
        </span>
      </button>
    </li>
  );
}
